/**
 * CMS-09 — the single-grant editor: create one grant by device / email / voucher, edit it,
 * revoke it, delete it logically and read its redemptions.
 *
 * Phase 1 (docs/07 §1) the product is always `plan_full`, so it is shown as a read-only line
 * instead of a picker and neither price nor currency appears.
 */
import type {
  CmsGrantCreateBodyInput,
  CmsGrantDetailResponse,
  GrantDTO,
  GrantMatchType,
  GrantTxType,
} from '@akpl/shared';
import { grantMatchValueIssue, PHASE1_PRODUCT_ID } from '@akpl/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api } from '../../api';
import { useT } from '../../i18n';
import { formatDateLong, formatDateTime } from '../../lib/format';
import { Button } from '../ui/button';
import { useConfirm } from '../ui/confirm';
import { Drawer } from '../ui/drawer';
import { Field, Input, Select, Textarea } from '../ui/field';
import { Badge } from '../ui/panel';
import { useToast } from '../ui/toast';
import { GrantStatusBadge } from './badges';

const TX_TYPES: GrantTxType[] = ['retail', 'bundle', 'gift', 'promo', 'manual'];

export interface GrantDraft {
  matchType: GrantMatchType;
  matchValue: string;
  durationDays: string;
  txType: GrantTxType;
  externalRef: string;
  maxRedemptions: string;
  validUntil: string;
  notes: string;
}

export function draftFromGrant(grant: GrantDTO | null, matchType: GrantMatchType): GrantDraft {
  return {
    matchType: grant?.matchType ?? matchType,
    matchValue: grant?.matchValue ?? '',
    durationDays: grant?.durationDays == null ? '' : String(grant.durationDays),
    txType: grant?.txType ?? 'manual',
    externalRef: grant?.externalRef ?? '',
    maxRedemptions: String(grant?.maxRedemptions ?? 1),
    validUntil: grant?.validUntil ? grant.validUntil.slice(0, 10) : '',
    notes: grant?.notes ?? '',
  };
}

/**
 * `'perpetual'` is a choice the operator makes, not what is left when the field is blank: the
 * form used to send `durationDays: null` for an empty input and never said so (QA 2026-09-15
 * UX-205).
 */
export type DurationMode = 'perpetual' | 'days';

export function durationModeOf(draft: GrantDraft): DurationMode {
  return draft.durationDays.trim() === '' ? 'perpetual' : 'days';
}

/** Default when the operator picks "Días" and has not typed a number yet. */
export const DEFAULT_DURATION_DAYS = '30';

/**
 * The reason "Crear grant" stays disabled, or `null` when the draft can be sent. Same rule the
 * contract enforces (`grantMatchValueIssue`), so the console never posts a body the API rejects.
 */
export function grantDraftIssue(
  draft: GrantDraft,
  mode: DurationMode,
): 'missing_match_value' | 'invalid_email' | 'invalid_voucher' | 'invalid_duration' | null {
  if (draft.matchValue.trim() === '') return 'missing_match_value';
  const matchIssue = grantMatchValueIssue(draft.matchType, draft.matchValue);
  if (matchIssue) return matchIssue;
  if (mode === 'days' && !(Number(draft.durationDays) >= 1)) return 'invalid_duration';
  return null;
}

/** `''` → `null`; a date input gives `YYYY-MM-DD`, the contract wants a full ISO stamp. */
function isoDate(value: string): string | null {
  if (!value.trim()) return null;
  const ms = Date.parse(`${value}T00:00:00.000Z`);
  return Number.isNaN(ms) ? null : new Date(ms).toISOString();
}

export function grantCreateBody(draft: GrantDraft): CmsGrantCreateBodyInput {
  return {
    mode: 'single',
    matchType: draft.matchType,
    matchValue: draft.matchValue.trim(),
    durationDays: draft.durationDays.trim() ? Number(draft.durationDays) : null,
    txType: draft.txType,
    externalRef: draft.externalRef.trim() || null,
    maxRedemptions: Number(draft.maxRedemptions) || 1,
    validUntil: isoDate(draft.validUntil),
    notes: draft.notes.trim() || null,
  };
}

/** Only the fields the operator touched travel in the PATCH (docs/06 §4). */
export function grantPatchBody(draft: GrantDraft, grant: GrantDTO): Record<string, unknown> {
  const next = grantCreateBody(draft) as Record<string, unknown>;
  const patch: Record<string, unknown> = {};
  const current: Record<string, unknown> = {
    matchValue: grant.matchValue,
    durationDays: grant.durationDays,
    txType: grant.txType,
    externalRef: grant.externalRef,
    maxRedemptions: grant.maxRedemptions,
    validUntil: grant.validUntil,
    notes: grant.notes,
  };
  for (const key of Object.keys(current)) {
    if (JSON.stringify(next[key]) !== JSON.stringify(current[key])) patch[key] = next[key];
  }
  return patch;
}

export function GrantDrawer({
  grant,
  canEdit,
  onClose,
}: {
  grant: GrantDTO | 'new';
  canEdit: boolean;
  onClose: () => void;
}) {
  const { t, i18n } = useT();
  const locale = i18n.language;
  const queryClient = useQueryClient();
  const isNew = grant === 'new';
  const current = isNew ? null : grant;
  const [draft, setDraft] = useState<GrantDraft>(() => draftFromGrant(current, 'email'));
  const [durationMode, setDurationMode] = useState<DurationMode>(() =>
    durationModeOf(draftFromGrant(current, 'email')),
  );
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * QA 2026-09-15 **UX-562**: creating a grant and revoking one said **nothing at all** — 44
   * samples over nine seconds with an empty live region — while every *destructive* action in
   * the console announced itself with a «Deshacer» toast. The only feedback was the drawer
   * closing and a row appearing in a fifty-row list which, under any sort or filter but the
   * default, is not on screen. Every outcome now goes through the same polite region.
   */
  const toast = useToast();

  const issue = grantDraftIssue(draft, durationMode);
  // Only complain about what the operator already typed — an untouched empty field is not an error.
  const fieldError =
    touched && issue && issue !== 'invalid_duration'
      ? t(`cms:grants.form.${issue}` as 'cms:grants.form.invalid_email')
      : null;
  const durationError =
    issue === 'invalid_duration' ? t('cms:grants.form.invalid_duration') : undefined;

  const detail = useQuery<CmsGrantDetailResponse>({
    queryKey: ['cms', 'grant', current?.id],
    queryFn: () => api.cmsGrant({ params: { id: current!.id } }),
    enabled: !isNew,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['cms', 'grants'] });
    if (current) void queryClient.invalidateQueries({ queryKey: ['cms', 'grant', current.id] });
  };

  const save = useMutation({
    mutationFn: async () => {
      if (isNew) return api.cmsGrantCreate({ body: grantCreateBody(draft) });
      const patch = grantPatchBody(draft, current!);
      if (Object.keys(patch).length === 0) return null;
      return api.cmsGrantPatch({ params: { id: current!.id }, body: patch as never });
    },
    onSuccess: (result) => {
      invalidate();
      // `null` is "nothing changed": a PATCH with an empty body is never sent, so there is
      // nothing to announce either.
      if (result !== null) {
        const name = draft.matchValue.trim() || current?.matchValue || '';
        toast({
          message: isNew
            ? t('cms:grants.toast.created', { name })
            : t('cms:grants.toast.updated', { name }),
        });
      }
      onClose();
    },
    onError: () => setError(t('common:states.error')),
  });

  const confirm = useConfirm();

  const revoke = useMutation({
    mutationFn: () => api.cmsGrantRevoke({ params: { id: current!.id }, body: {} }),
    onSuccess: () => {
      invalidate();
      toast({ message: t('cms:grants.toast.revoked', { name: current!.matchValue }) });
      onClose();
    },
    onError: () => setError(t('common:states.error')),
  });

  const remove = useMutation({
    mutationFn: () => api.cmsGrantDelete({ params: { id: current!.id } }),
    onSuccess: () => {
      invalidate();
      toast({
        message: t('cms:grants.toast.deleted', { name: current!.matchValue }),
        tone: 'danger',
      });
      onClose();
    },
    onError: () => setError(t('common:states.error')),
  });

  /** Revoking kills a live entitlement and deleting drops the record: both ask first (UX-211). */
  const onRevoke = async () => {
    const ok = await confirm({
      title: t('cms:confirm.revokeGrant.title'),
      body: t('cms:confirm.revokeGrant.body'),
      confirmLabel: t('cms:confirm.revokeGrant.confirm'),
      tone: 'danger',
    });
    if (ok) revoke.mutate();
  };

  const onDelete = async () => {
    const ok = await confirm({
      title: t('cms:confirm.deleteGrant.title'),
      body: t('cms:confirm.deleteGrant.body'),
      confirmLabel: t('cms:confirm.deleteGrant.confirm'),
      tone: 'danger',
    });
    if (ok) remove.mutate();
  };

  const helpKey =
    draft.matchType === 'device'
      ? 'cms:grants.form.helpDevice'
      : draft.matchType === 'email'
        ? 'cms:grants.form.helpEmail'
        : 'cms:grants.form.helpVoucher';

  return (
    <Drawer
      open
      onClose={onClose}
      title={isNew ? t('cms:grants.form.createTitle') : t('cms:grants.form.editTitle')}
      subtitle={current ? current.matchValue : undefined}
      footer={
        <>
          {current && canEdit && (
            <Button
              variant="link"
              size="sm"
              className="mr-auto text-danger"
              disabled={remove.isPending}
              onClick={() => void onDelete()}
            >
              {t('common:actions.delete')}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          {canEdit && (
            <Button
              data-testid="grant-save"
              disabled={issue !== null || save.isPending}
              onClick={() => save.mutate()}
            >
              {isNew ? t('cms:grants.form.create') : t('cms:grants.form.save')}
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {current && (
          <div className="flex items-center gap-2">
            <GrantStatusBadge status={current.status} />
            <span className="text-[12px] text-muted">
              {t('cms:grants.columns.redemptions')}: {current.redemptions}/{current.maxRedemptions}
            </span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('cms:grants.form.matchType')}>
            {(id) => (
              <Select
                id={id}
                data-testid="grant-match-type"
                value={draft.matchType}
                disabled={!isNew}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, matchType: e.target.value as GrantMatchType }))
                }
              >
                <option value="device">{t('cms:grants.tabs.device')}</option>
                <option value="email">{t('cms:grants.tabs.email')}</option>
                <option value="voucher">{t('cms:grants.tabs.voucher')}</option>
              </Select>
            )}
          </Field>
          <Field label={t('cms:grants.form.txType')}>
            {(id) => (
              <Select
                id={id}
                value={draft.txType}
                onChange={(e) => setDraft((d) => ({ ...d, txType: e.target.value as GrantTxType }))}
              >
                {TX_TYPES.map((tx) => (
                  <option key={tx} value={tx}>
                    {t(`cms:grants.tx.${tx}` as 'cms:grants.tx.manual')}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>

        <Field
          label={t('cms:grants.form.matchValue')}
          help={t(helpKey as 'cms:grants.form.helpEmail')}
          error={fieldError ?? undefined}
        >
          {(id) => (
            <Input
              id={id}
              data-testid="grant-match-value"
              value={draft.matchValue}
              aria-invalid={fieldError ? true : undefined}
              onChange={(e) => {
                setTouched(true);
                setDraft((d) => ({ ...d, matchValue: e.target.value }));
              }}
              onBlur={() => setTouched(true)}
            />
          )}
        </Field>

        <Field label={t('cms:grants.form.product')} help={t('cms:grants.form.productHelp')}>
          {(id) => <Input id={id} value={PHASE1_PRODUCT_ID} readOnly disabled />}
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field
            label={t('cms:grants.form.duration')}
            help={t('cms:grants.form.durationHelp')}
            error={durationError}
          >
            {(id) => (
              <div className="flex items-center gap-2">
                <Select
                  id={id}
                  data-testid="grant-duration-mode"
                  className="w-32"
                  value={durationMode}
                  onChange={(e) => {
                    const next = e.target.value as DurationMode;
                    setDurationMode(next);
                    setDraft((d) => ({
                      ...d,
                      durationDays: next === 'perpetual' ? '' : DEFAULT_DURATION_DAYS,
                    }));
                  }}
                >
                  <option value="perpetual">{t('cms:grants.perpetual')}</option>
                  <option value="days">{t('cms:grants.form.durationDaysOption')}</option>
                </Select>
                {durationMode === 'days' && (
                  <Input
                    type="number"
                    min={1}
                    aria-label={t('cms:grants.form.durationDays')}
                    value={draft.durationDays}
                    onChange={(e) => setDraft((d) => ({ ...d, durationDays: e.target.value }))}
                  />
                )}
              </div>
            )}
          </Field>
          <Field label={t('cms:grants.form.maxRedemptions')}>
            {(id) => (
              <Input
                id={id}
                type="number"
                min={1}
                value={draft.maxRedemptions}
                onChange={(e) => setDraft((d) => ({ ...d, maxRedemptions: e.target.value }))}
              />
            )}
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field
            label={t('cms:grants.form.validUntil')}
            // UX-210: `lang` asks Chromium to paint the widget in the console's locale, and the
            // help line spells the chosen day out so `mm/dd` vs `dd/mm` cannot be misread.
            help={
              draft.validUntil
                ? formatDateLong(draft.validUntil, locale)
                : t('cms:grants.form.validUntilHelp')
            }
          >
            {(id) => (
              <Input
                id={id}
                type="date"
                lang={locale}
                value={draft.validUntil}
                onChange={(e) => setDraft((d) => ({ ...d, validUntil: e.target.value }))}
              />
            )}
          </Field>
          <Field label={t('cms:grants.form.externalRef')}>
            {(id) => (
              <Input
                id={id}
                value={draft.externalRef}
                onChange={(e) => setDraft((d) => ({ ...d, externalRef: e.target.value }))}
              />
            )}
          </Field>
        </div>

        <Field label={t('cms:grants.form.notes')}>
          {(id) => (
            <Textarea
              id={id}
              rows={2}
              value={draft.notes}
              onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
            />
          )}
        </Field>

        {current && canEdit && current.status !== 'revoked' && (
          <div>
            <Button
              variant="secondary"
              size="sm"
              disabled={revoke.isPending}
              onClick={() => void onRevoke()}
            >
              {t('cms:grants.revoke')}
            </Button>
          </div>
        )}

        {current && (
          <section
            className="mt-2 rounded-[12px] border border-line"
            data-testid="grant-redemptions"
          >
            <header className="flex items-center justify-between border-b border-line px-3 py-2">
              <h3 className="text-[13px] font-bold text-ink">
                {t('cms:grants.redemptions.title')}
              </h3>
              <Badge>{detail.data?.redemptions.length ?? 0}</Badge>
            </header>
            {detail.data && detail.data.redemptions.length > 0 ? (
              <ul className="divide-y divide-line">
                {detail.data.redemptions.map((redemption) => (
                  <li key={redemption.id} className="px-3 py-2">
                    <p className="text-[13px] font-semibold text-ink">
                      {redemption.userEmail ?? redemption.userId}
                    </p>
                    <p className="text-[12px] text-muted">
                      {formatDateTime(redemption.redeemedAt, locale)} ·{' '}
                      {t(`cms:grants.trigger.${redemption.trigger}` as 'cms:grants.trigger.login')}
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="px-3 py-3 text-[12px] text-muted">
                {t('cms:grants.redemptions.empty')}
              </p>
            )}
          </section>
        )}

        {error && (
          <p className="text-[12px] text-danger" role="alert">
            {error}
          </p>
        )}
      </div>
    </Drawer>
  );
}
